import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/auth/AuthContext";

function fmtDate(s) {
  if (!s) return null;
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return s; }
}


export default function PublicGallery() {
  const { slug } = useParams();
  const [status, setStatus] = useState("loading"); // "loading" | "error" | "ok"
  const [wedding, setWedding] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [ownerPlan, setOwnerPlan] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const touchStartX = useRef(null);
  const swipedRef = useRef(false);

  // Fetch gallery data
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setStatus("loading");
      const { data, error } = await supabase.rpc("get_public_gallery_by_slug", { p_slug: slug });
      if (cancelled) return;
      if (error || !data) {
        setStatus("error");
        return;
      }
      if (data.error === "not_found" || data.error === "plan_required") {
        setStatus("error");
        return;
      }
      setWedding(data.wedding);
      setPhotos(data.photos || []);
      setOwnerPlan(data.owner_plan);
      setStatus("ok");
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const n = photos.length;
    const handler = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") setLightboxIndex(i => i !== null ? (i - 1 + n) % n : null);
      if (e.key === "ArrowRight") setLightboxIndex(i => i !== null ? (i + 1) % n : null);
    };
    if (lightboxIndex !== null) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIndex, photos.length]);

  const lightboxItem = lightboxIndex !== null ? photos[lightboxIndex] : null;

  async function downloadSingle(photo) {
    try {
      const res = await fetch(photo.public_url);
      const blob = await res.blob();
      const rawExt = blob.type.split("/")[1] || (photo.resource_type === "video" ? "mp4" : "jpg");
      const ext = rawExt === "quicktime" ? "mov" : rawExt === "x-msvideo" ? "avi" : rawExt;
      const name = photo.guest_label ? `${photo.guest_label}.${ext}` : `foto.${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  }

  async function downloadZip() {
    if (downloadingZip || photos.length === 0) return;
    setDownloadingZip(true);
    try {
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i];
        try {
          const res = await fetch(p.public_url);
          const blob = await res.blob();
          const ext = blob.type.split("/")[1] || "bin";
          const name = p.guest_label ? `${p.guest_label}.${ext}` : `${p.resource_type === "video" ? "video" : "foto"}-${i + 1}.${ext}`;
          zip.file(name, blob);
        } catch {
          // skip individual files that fail
        }
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `galleria-${slug}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // fail silently — no toast on public page
    } finally {
      setDownloadingZip(false);
    }
  }

  // Loading state
  if (status === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "2px solid #e5e5e5",
          borderTopColor: "#c9a86a",
          animation: "spin 0.8s linear infinite",
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <div style={{ minHeight: "100vh", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "#9ca3af", fontSize: 18, fontWeight: 300 }}>Galleria non disponibile.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      {/* Header */}
      <header style={{ padding: "40px 24px 32px", maxWidth: 1240, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h1 style={{
              fontFamily: "Georgia, serif",
              fontSize: "clamp(28px, 6vw, 56px)",
              fontWeight: 300,
              letterSpacing: "-0.02em",
              color: "#0a0a0a",
              margin: 0,
              lineHeight: 1.05,
            }}>
              {wedding.title}
            </h1>
            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "6px 16px", color: "#9ca3af", fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}>
              {wedding.wedding_date && <span>{fmtDate(wedding.wedding_date)}</span>}
              {wedding.location && <span>{wedding.location}</span>}
              <span>{photos.length} {photos.length === 1 ? "ricordo" : "ricordi"}</span>
            </div>
          </div>
          {ownerPlan === "premium" && (
            <button
              type="button"
              onClick={downloadZip}
              disabled={downloadingZip}
              style={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "10px 18px",
                borderRadius: 9999,
                border: "1px solid rgba(201,168,106,0.5)",
                background: "transparent",
                color: "#9b7d40",
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                cursor: downloadingZip ? "default" : "pointer",
                opacity: downloadingZip ? 0.6 : 1,
              }}
            >
              {downloadingZip ? "Preparazione…" : "⬇ Scarica tutto"}
            </button>
          )}
        </div>
      </header>

      {/* Photo grid */}
      <main style={{ padding: "0 24px 60px", maxWidth: 1240, margin: "0 auto" }}>
        {photos.length === 0 ? (
          <div style={{ border: "1px dashed #e5e5e5", borderRadius: 16, padding: "64px 24px", textAlign: "center", color: "#9ca3af", fontSize: 14 }}>
            Ancora nessun ricordo in questa galleria.
          </div>
        ) : (
          <div style={{ columns: "2 160px", columnGap: 8 }}>
            {photos.map((photo, idx) => (
              <div
                key={photo.id}
                onClick={() => setLightboxIndex(idx)}
                role="button"
                tabIndex={0}
                aria-label={photo.guest_label || `Ricordo ${idx + 1}`}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLightboxIndex(idx); } }}
                style={{
                  position: "relative",
                  marginBottom: 8,
                  overflow: "hidden",
                  borderRadius: 12,
                  background: "#f3f3f3",
                  cursor: "pointer",
                  breakInside: "avoid",
                }}
              >
                {photo.resource_type === "video" ? (
                  <video
                    src={photo.public_url + "#t=0.1"}
                    style={{ width: "100%", display: "block", objectFit: "cover" }}
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }}
                  />
                ) : (
                  <img
                    src={photo.public_url}
                    alt={photo.guest_label || "Ricordo"}
                    loading="lazy"
                    style={{ width: "100%", display: "block", objectFit: "cover" }}
                  />
                )}

                {/* Guest label overlay */}
                {photo.guest_label && (
                  <div style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)",
                    padding: "20px 10px 8px",
                  }}>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.9)" }}>{photo.guest_label}</span>
                  </div>
                )}

                {/* Video badge */}
                {photo.resource_type === "video" && (
                  <div style={{ position: "absolute", top: 6, right: 6 }}>
                    <span style={{
                      fontSize: 9,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      padding: "2px 6px",
                      borderRadius: 9999,
                      background: "rgba(0,0,0,0.6)",
                      color: "#fff",
                    }}>Video</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Branding footer (Smart only) */}
      {ownerPlan === "smart" && (
        <footer style={{ textAlign: "center", padding: "24px 16px 40px", borderTop: "1px solid #f0f0f0" }}>
          <a
            href="https://wedflow.me"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, color: "#9ca3af", letterSpacing: "0.1em", textDecoration: "none" }}
          >
            Powered by WedFlow
          </a>
        </footer>
      )}

      {/* Lightbox */}
      {lightboxItem && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.93)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => { if (swipedRef.current) { swipedRef.current = false; return; } setLightboxIndex(null); }}
          onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const delta = e.changedTouches[0].clientX - touchStartX.current;
            const n = photos.length;
            if (Math.abs(delta) > 50) {
              setLightboxIndex(i => delta < 0 ? (i + 1) % n : (i - 1 + n) % n);
              swipedRef.current = true;
            }
            touchStartX.current = null;
          }}
        >
          {/* Close */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            style={{ position: "absolute", top: 16, right: 16, width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
          >✕</button>

          {/* Left arrow */}
          {photos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + photos.length) % photos.length); }}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
            >‹</button>
          )}

          {/* Right arrow */}
          {photos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % photos.length); }}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
            >›</button>
          )}

          {/* Media */}
          {lightboxItem.resource_type === "video" ? (
            <video
              key={lightboxItem.id}
              src={lightboxItem.public_url}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "85vw", maxHeight: "85vh", borderRadius: 12 }}
            />
          ) : (
            <img
              key={lightboxItem.id}
              src={lightboxItem.public_url}
              alt={lightboxItem.guest_label || ""}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "85vw", maxHeight: "85vh", objectFit: "contain", borderRadius: 12 }}
            />
          )}

          {/* Counter */}
          <div style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.6)", fontSize: 12, pointerEvents: "none" }}>
            {lightboxIndex + 1} / {photos.length}
          </div>

          {/* Download button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); downloadSingle(lightboxItem); }}
            style={{
              position: "absolute",
              bottom: 12,
              right: 16,
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "7px 14px",
              borderRadius: 9999,
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "#fff",
              fontSize: 12,
              cursor: "pointer",
              zIndex: 1,
            }}
          >
            ⬇ Scarica
          </button>
        </div>
      )}
    </div>
  );
}
